import { createContext, ReactNode, useCallback, useContext } from "react";

interface LeadNavigationContextType {
  triggerNextLead: (closeOnEnd?: boolean) => void;
  triggerPreviousLead: () => void;
  navigateBack: () => void;
  notesOpen: boolean;
  toggleNotes: () => void;
}

const LeadNavigationContext = createContext<LeadNavigationContextType | null>(
  null,
);

export function useLeadNavigation() {
  const context = useContext(LeadNavigationContext);
  if (!context) {
    throw new Error(
      "useLeadNavigation must be used within a LeadNavigationProvider",
    );
  }
  return context;
}

interface LeadNavigationProviderProps {
  children: ReactNode;
  onNextLead: (closeOnEnd?: boolean) => void;
  onPreviousLead: () => void;
  onNavigateBack: () => void;
  notesOpen: boolean;
  onToggleNotes: () => void;
}

export function LeadNavigationProvider({
  children,
  onNextLead,
  onPreviousLead,
  onNavigateBack,
  notesOpen,
  onToggleNotes,
}: LeadNavigationProviderProps) {
  const triggerNextLead = useCallback(
    (closeOnEnd: boolean = false) => {
      onNextLead(closeOnEnd);
    },
    [onNextLead],
  );

  const triggerPreviousLead = useCallback(() => {
    onPreviousLead();
  }, [onPreviousLead]);

  const navigateBack = useCallback(() => {
    onNavigateBack();
  }, [onNavigateBack]);

  const toggleNotes = useCallback(() => {
    onToggleNotes();
  }, [onToggleNotes]);

  const value = {
    triggerNextLead,
    triggerPreviousLead,
    navigateBack,
    notesOpen,
    toggleNotes,
  };

  return (
    <LeadNavigationContext.Provider value={value}>
      {children}
    </LeadNavigationContext.Provider>
  );
}
